import {Action} from "/@lib@/commons/actions.js";
import {IPubCtx} from "/@lib@/wsp/wspMetaUi.js";
import {EGenStatus, ITEM} from "/@lib@/wsp/item.js";
import {JSrcFields, SRC, srcRef, srcUri} from "/@lib@/wsp/src.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {REG} from "/@lib@/commons/registry.js";
import {Wsp, WSP, wspCd, wspUri} from "/@lib@/wsp/wsp.js";
import {CID, CidProcess, CidSession, CidUploadStep, ECidSessionState, ICidWebUploadDef, OCidSessionConfig} from "/@lib@/core/cid.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {IO} from "/@lib@/commons/io/io.js";
import {CDM} from "/@lib@/commons/utils/cdm.js";
import {Generators} from "/@back@/wsp/views/generators.js";
import {Desk} from "/@lib@/commons/desk.js";
import {JCidEnPoints, JCidStoredProps} from "/@back@/core/widgets/cidUi.js";
import {LANG} from "/@lib@/commons/lang.js";

export class GenAction extends Action<IPubCtx> {

	_hideForStatuses: EGenStatus[];

	_disabledForStatuses: EGenStatus[];

	/** Bouton masqué en fonction du status */
	setHideForStatuses(statuses: string/*deprecated*/ | EGenStatus | EGenStatus[]): this {
		this._hideForStatuses = typeof statuses === 'string' ? statuses.split(",") as EGenStatus[] : statuses;
		return this;
	}

	/** Bouton désactivé en fonction du status */
	setDisabledForStatuses(statuses: EGenStatus | EGenStatus[]): this {
		this._disabledForStatuses = typeof statuses === 'string' ? [statuses] as EGenStatus[] : statuses;
		return this;
	}

	isVisible(ctx: IPubCtx): boolean {
		if (this._hideForStatuses && this._hideForStatuses.indexOf(ctx.genInfo?.status || EGenStatus.null) >= 0) return false;
		return super.isVisible(ctx);
	}

	isEnabled(ctx: IPubCtx): boolean {
		if (this._disabledForStatuses && this._disabledForStatuses.indexOf(ctx.genInfo?.status || EGenStatus.null) >= 0) return false;
		return super.isEnabled(ctx);
	}

}

export class GenerateAction extends GenAction {

	protected overrideGenProps: Dict<Jsonisable>;

	protected confirmMsg: string | null;

	constructor(id?: string) {
		super(id || "generate");
		this._label = "Générer";
		this._icon = id && id.startsWith("send") ? "/@skin@/wsp/views/generators/send.svg" : "/@skin@/wsp/views/generators/generate.svg";
		this._description = "Nouvelle génération";
	}

	setOverrideGenProps(props: Dict<Jsonisable>): this {
		this.overrideGenProps = props;
		return this;
	}

	setConfirmMsg(msg: string): this {
		this.confirmMsg = msg;
		return this;
	}

	/**
	 * Contrôle le superGen : retourne true si le contrôle est OK
	 */
	static async checkSuperGen(ctx: IPubCtx): Promise<boolean> {
		const idx = ctx.pubNode.codeGenStack.lastIndexOf('_');
		if (idx > 0) {
			//on est un sous-générateur, on vérifie l'état du gen parent...
			const superGenCode = ctx.pubNode.codeGenStack.substring(0, idx);
			const resp = await ITEM.fetchGenInfo(ctx.wsp, ctx.uiContext, SRC.srcRef(ctx.shortDesc), superGenCode);
			switch (resp?.status) {
			case EGenStatus.null: {
				const genTitle = resp.title;
				await POPUP.confirm(`Vous devez d'abord générer '${genTitle}'.`, ctx.uiContext, {cancelLbl: null});
				if (ctx.uiContext instanceof Generators) ctx.uiContext.setSrc(ctx.shortDesc, superGenCode);
				return false;
			}
			case EGenStatus.working: {
				const genTitle = resp.title;
				await POPUP.confirm(`La génération '${genTitle}' dont dépend celle-ci est en cours. Veuillez d'abord en attendre la fin.`, ctx.uiContext, {cancelLbl: null});
				if (ctx.uiContext instanceof Generators) ctx.uiContext.setSrc(ctx.shortDesc, superGenCode);
				return false;
			}
			case EGenStatus.warning: {
				const genTitle = resp.title;
				if (!await POPUP.confirm(`La génération '${genTitle}' dont dépend celle-ci contient des anomalies. Souhaitez-vous poursuivre ?`, ctx.uiContext)) {
					if (ctx.uiContext instanceof Generators) ctx.uiContext.setSrc(ctx.shortDesc, superGenCode);
					return false;
				}
				break;
			}
			case EGenStatus.failed: {
				const genTitle = resp.title;
				if (!await POPUP.confirm(`La génération '${genTitle}' dont dépend celle-ci a échoué. Souhaitez-vous vraiment poursuivre ?`, ctx.uiContext)) {
					if (ctx.uiContext instanceof Generators) ctx.uiContext.setSrc(ctx.shortDesc, superGenCode);
					return false;
				}
				break;
			}
			}
		}
		return true;
	}

	async execute(ctx: IPubCtx, ev?: Event): Promise<any> {
		if (!await GenerateAction.checkSuperGen(ctx))
			return;
		ctx.genProps().then(async (props: Jsonisable | null | false) => {
			if (props === false) return; //echec à la validation des genProps.
			if (this.overrideGenProps) {
				if (props) Object.assign(props, this.overrideGenProps);
				else props = this.overrideGenProps;
			}
			if (!this.confirmMsg || await POPUP.confirm(this.confirmMsg, ctx.uiContext)) {
				const genSvc: (wsp: Wsp, uiContext: HTMLElement, srcRef: srcRef, codeGenStack: string, props: Jsonisable, customFullUriDest?: string) => Promise<void> = ctx.reg.getSvc("gen.action.generate") || ITEM.generate;
				genSvc.call(this, ctx.wsp, ctx.uiContext, SRC.srcRef(ctx.shortDesc), ctx.pubNode.codeGenStack, props, ctx.customDestPath);
			}
		});
	}
}

export class ShowGenAction extends GenAction {

	constructor(id?: string, protected subPathToShow?: string, protected isWeb?: true | null) {
		super(id || "show");
		this._label = "Consulter";
		this._icon = "/@skin@/wsp/views/generators/show.svg";
		this._description = "Consulter la dernière génération";
		this._hideForStatuses = [EGenStatus.failed, EGenStatus.null];
	}

	isVisible(ctx: IPubCtx): boolean {
		if (!ctx.genInfo.uriPub) return false;
		return super.isVisible(ctx);
	}

	execute(ctx: IPubCtx, ev?: Event): any | 'noPreventDefault' {
		const genNature = this.isWeb ? "web" : ctx.pubNode.genNature;
		if (Desk.electron && ctx.genInfo.localPathPub && genNature !== "web") {
			window.postMessage({type: "client:modules:localServer:open", path: ctx.genInfo.localPathPub}, location.origin);
		} else {
			window.open(ITEM.getGenPubUrl(ctx.wsp, ctx.genInfo.uriPub + (this.subPathToShow ? `/${this.subPathToShow}` : "")), '_blank');
		}
	}
}

export class ShowGenInLOAction extends ShowGenAction {
	isVisible(ctx: IPubCtx): boolean {
		if (!Desk.electron || !ctx.genInfo.uriPub) return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: IPubCtx, ev?: Event): Promise<any | 'noPreventDefault'> {
		const {env} = ctx.reg;
		if (ctx.genInfo.localPathPub) {
			window.postMessage({type: "client:modules:localServer:open", path: ctx.genInfo.localPathPub}, location.origin);
		} else {
			const url = ITEM.getGenPubUrl(ctx.wsp, ctx.genInfo.uriPub);
			const openReply = await IO.sendMessage({type: "client:modules:odEditor:open", url: encodeURI(url)});
			if (openReply.type == 'error') ERROR.log(openReply.msg);
			else if (openReply.type === "notFound") POPUP.showNotifInfo("L'éditeur de document 'OpenDocument' (LibreOffice, ...) n'a pu être trouvé.", env.uiRoot);
		}
	}
}

export class DownloadGenAction extends GenAction {

	constructor(id?: string) {
		super(id || "download");
		this._label = "Télécharger";
		this._icon = "/@skin@/wsp/views/generators/download.svg";
		this._description = "Télécharger la dernière génération";
		this._hideForStatuses = [EGenStatus.failed, EGenStatus.null];
	}

	isVisible(ctx: IPubCtx): boolean {
		if (!ctx.genInfo.mimeDownload) return false;
		return super.isVisible(ctx);
	}

	execute(ctx: IPubCtx, ev?: Event): any | 'noPreventDefault' {
		const dwnSvc: (wsp: Wsp, srcRef: srcRef, codeGenStack: string, customFullUriDest?: string) => string = ctx.reg.getSvc("gen.action.download") || ITEM.getGenDownloadUrl;
		const link = document.createElement('a');
		link.href = dwnSvc.call(this, ctx.wsp, SRC.srcRef(ctx.shortDesc), ctx.pubNode.codeGenStack, ctx.customDestPath);
		link.download = "";
		link.click();
	}
}

export class LogGenAction extends GenAction {

	constructor(id?: string) {
		super(id || "log");
		this._label = "Rapport";
		this._icon = "/@skin@/wsp/views/generators/log.svg";
		this._description = "Consulter les traces";
	}

	isVisible(ctx: IPubCtx): boolean {
		if (!ctx.genInfo?.uriTraces) return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: IPubCtx & { msg?: string }, ev?: Event): Promise<any | 'noPreventDefault'> {
		const traceDocUrl = ctx.wsp.wspServer.config.pubUrl.resolve(ctx.genInfo.uriTraces.substr(1));
		const traceDoc = await traceDocUrl.fetchDom();
		await ERROR.showError({
			msg: ctx.msg || "Une erreur est survenue lors de la génération.",
			technicalDetails: this.technicalDetailsFromGenTrace(traceDoc),
			adminDetails: DOM.ser(DOM.indentDom(traceDoc)),
			ctx
		});
	}

	/**
	 * Crée une liste HTML à partir d'une trace de generation
	 */
	technicalDetailsFromGenTrace(traceNode: Document | Element): HTMLElement {
		if (DOM.IS_document(traceNode)) return this.technicalDetailsFromGenTrace(traceNode.documentElement);
		let item: HTMLElement;
		if (traceNode.localName == 'trace') {
			item = <ul class="msgList"/>;
			for (const child of traceNode.children) {
				if (child.localName == 'l') item.appendChild(this.technicalDetailsFromGenTrace(child.firstElementChild));
			}
		} else if (traceNode.localName == 'message') {
			let desc = traceNode.getAttribute('desc');
			let v = traceNode.getAttribute('var1');
			if (v != null) {
				const vars = [v];
				v = traceNode.getAttribute(`var2`);
				let i = 3;
				while (v != null) {
					vars.push(v);
					v = traceNode.getAttribute(`var${i++}`);
				}
				desc = LANG.formatStr(desc, vars);
			}
			item = <li><span>{desc}</span></li> as HTMLLIElement;
			const level = traceNode.getAttribute('type');
			if (level == 'Warning') item.classList.add('warn');
			else if (level == 'Error' || level == 'Exception') item.classList.add('error');

			const childMessages = document.createDocumentFragment();
			for (const child of traceNode.children) {
				if (child.localName == 'message') childMessages.appendChild(this.technicalDetailsFromGenTrace(child));
			}
			if (childMessages.children.length) item.appendChild(<ul class="msgList">{childMessages}</ul>);
		}
		return item;
	}
}


REG.reg.addToList("actions:gen", "generate", 1, new GenerateAction(), 0);
REG.reg.addToList("actions:gen", "show", 1, new ShowGenAction(), 10);
REG.reg.addToList("actions:gen", "download", 1, new DownloadGenAction(), 20);
//REG.reg.addToList("actions:gen", "reveal", 1, new RevealGenAction(), 30);
REG.reg.addToList("actions:gen", "log", 1, new LogGenAction(), 40);

export interface JWspGenMsg {
	svc: 'wspGen'
	type: 'genStart' | 'genEnd'
	wsp: wspCd
	srcUri: srcUri
	codeGenStack: string
	/** dest path libre de génération. Usage : styler */
	customDestPath?: wspUri
}


export class CidAction extends Action<IPubCtx> {

	/**
	 * Définition par défaut des process de déploiement.
	 * Note : par défaut, le déploiement d'un accueil "scenari.eu:cid:deployIndexAction" n'est pas recherché,
	 * il faut configurer un bouton explicitement pour cet usage.
	 */
	static PRC_IS_DEPLOY = ["scenari.eu:cid:deployResourceAction", "http://schema.org/TransferAction", "http://schema.org/SendAction"];

	/** Définition par défaut des process de consult.*/
	static PRC_IS_CONSULT = ["scenari.eu:cid:consultResourceAction", "http://schema.org/ConsumeAction", "http://schema.org/ViewAction"];

	/** Restreint les proccess disponibles
	 * 		@see filterIsOr */
	processFilter: string[] | string[][] | ((ctx: IPubCtx) => CidProcess[]);

	/** Ignore les steps d'interactions optionnelles. **/
	passOptionalInteraction?: boolean

	setProcessFilter(processFilter: string[] | ((ctx: IPubCtx) => CidProcess[])): this {
		this.processFilter = processFilter;
		return this;
	}

	setPassOptionalInteraction(pass: boolean): this {
		this.passOptionalInteraction = pass;
		return this;
	}

	createMetasIn(ctx: IPubCtx): Dict<string> {
		return {
			scChainOrigin: JSON.stringify({serverUrl: ctx.wsp.wspServer.chain.config.universeUrl, wspCode: ctx.wsp.code, refUri: SRC.srcRef(ctx.shortDesc)})
		};
	}

	/** Si retour false, l'action est annulée. */
	async overrideMetas(ctx: IPubCtx, metasIn: Dict<any>, foreignMetas: { is: string[], value: string | ((session: CidSession) => string) }[]): Promise<void | boolean> {
	}

	/** Surcharge la config de la session. */
	overrideSession(config: OCidSessionConfig, ctx: IPubCtx): OCidSessionConfig {
		let processes = ctx.cidInfo.cidProcesses || ctx.cidInfo.lastManifest.processes;
		if (Array.isArray(this.processFilter)) {
			processes = Array.from(CID.filterIsOr(processes, this.processFilter));
		} else if (typeof this.processFilter === "function") {
			processes = this.processFilter(ctx);
		}
		if (processes.length === 1) {
			config.process = processes[0];
		} else {
			config.processes = processes;
		}
		if (this.passOptionalInteraction != null) config.cidPassOptionalInteraction = this.passOptionalInteraction;
		return config;
	}
}


interface JCidEnPointsFields extends JSrcFields {
	cidEndPoints?: JCidEnPoints
}


export abstract class CidDeployAction extends CidAction {
	_label = "Déployer";
	_icon = "/@skin@/wsp/views/generators/send.svg";

	protected confirmMsg: string | null;

	constructor() {
		super();
		this.processFilter = CidAction.PRC_IS_DEPLOY;
	}

	setConfirmMsg(msg: string): this {
		this.confirmMsg = msg;
		return this;
	}

	isEnabled(ctx: IPubCtx): boolean {
		if (ctx.cidInfo.cidSession && !ctx.cidInfo.cidSession.isClosed()) return false;
		return super.isEnabled(ctx);
	}

	async execute(ctx: IPubCtx, ev?: Event) {
		if (!this.confirmMsg || await POPUP.confirm(this.confirmMsg, ctx.uiContext)) {
			if (ctx.cidInfo.cidSession) ctx.cidInfo.cidSession.abortSession();
			await ctx.reg.env.universe.wspServer.wspsLive.saveAllHouses();
			try {
				ctx = clonePubCtx(ctx);
				ctx.cidInfo.cidSession = await this.createCidSession(ctx);
				if (!ctx.cidInfo.cidSession) return;
				await this.launchSession(ctx);
				if (ctx.cidInfo.cidSession.state === ECidSessionState.ended) this.saveLastDeploy(ctx);
			} finally {
				ctx.cidInfo.cidSession = null;
			}
		}
	}

	async launchSession(ctx: IPubCtx) {
		ctx.cidInfo.cidSession.onStateChange.add(() => (ctx.uiContext as Generators).cidRefreshOnSessionChange());
		await ctx.cidInfo.cidSession.doSession();
		const st = ctx.cidInfo.cidSession.state;
		if (st === ECidSessionState.failed) {
			const technicalDetails = ctx.cidInfo.cidSession.failedHttpCode ? `Failed CID http code : ${ctx.cidInfo.cidSession.failedHttpCode}` : null;
			ERROR.reportError({
				msg: ctx.cidInfo.cidSession.failedHttpCode === 403 ? "Vous ne disposez pas des autorisations nécessaires pour effectuer cette action." : "Échec au déploiement.",
				details: ctx.cidInfo.cidSession.failedReason || CID.cidErrorToMsg(ctx.cidInfo.cidSession.failedError),
				technicalDetails
			});
		} else if (st === ECidSessionState.ended) {
			//Temporaire : en attente de la mémorisation et de l'affichage du dernier état d'envoi.
			POPUP.showNotif("Déploiement réalisé avec succès", ctx.uiContext);
		}
	}

	abstract createCidSession(ctx: IPubCtx): Promise<CidSession>;

	abstract saveLastDeploy(ctx: IPubCtx): Promise<void>;
}

export class CidScarDeployAction extends CidDeployAction {

	public scarScope: "net" | "node";
	public scarFormat: "jar" | "zip" /*| "stream" non : mimetype alors dynamique. */;
	public scarMode: "wspTree" | "flat" | "rootAndRes";
	public scarLink: "absolute" | "relative";
	/** Uris complémentaires à exporter, ajoutées en plus de l'item courant du contexte */
	private addUris?: srcRef[];

	setScope(scarScope: "net" | "node"): this {
		this.scarScope = scarScope;
		return this;
	}

	addUriToExport(...addUris: srcRef[]): this {
		this.addUris = addUris;
		return this;
	}

	async createCidSession(ctx: IPubCtx): Promise<CidSession> {
		const fileName = ITEM.extractItemCode(ctx.shortDesc.srcUri).replace(/\..*/, ""); //code item sans extension
		const metasIn = this.createMetasIn(ctx);
		const foreignMetas = [{is: CID.IS_FileName, value: fileName}, {is: CID.IS_ContentType, value: "application/java-archive"}];
		if (await this.overrideMetas(ctx, metasIn, foreignMetas) === false) return;

		return new CidSession(this.overrideSession({
			manifest: ctx.cidInfo.lastManifest,
			uiHandler: ctx.uiContext as Generators,
			metasIn,
			foreignMetas,
			customUpload: async (session: CidSession, uploadStep: CidUploadStep, webUpload: ICidWebUploadDef): Promise<null | Response> => {
				const sendBySrv = await CidScarDeployAction.getSendByServerBody(ctx, session, uploadStep, webUpload);
				const params = IO.qs("cdaction", sendBySrv ? "SendTo" : "Export",
					"scope", this.scarScope || "net",
					"format", this.scarFormat || "jar",
					"mode", this.scarMode || "wspTree",
					"link", this.scarLink || "absolute",
					"downloadFileName", fileName,
					"param", ctx.reg.env.wsp.code,
					"refUris", this.addUris ? [SRC.srcRef(ctx.shortDesc)].concat(this.addUris).join('\t') : SRC.srcRef(ctx.shortDesc),
				);
				const content = await ctx.reg.env.wsp.wspServer.config.exportUrl.fetch(params, "none", sendBySrv ? {method: "PUT", body: JSON.stringify(sendBySrv)} : null);
				if (!content.ok) return content;//new Response(null, {status: content.status}) ?
				if (sendBySrv) return content; //Envoi par le serveur ok.
				session.config.bodyToUpload = await content.blob();
				return null; //déploiement standard à partir de bodyToUpload
			}
		}, ctx));
	}

	async saveLastDeploy(ctx: IPubCtx): Promise<void> {
		try {
			if (!ctx.wsp.isAvailable) return; //rupture de co => abandon
			let previousCidEndPoints = ctx.cidInfo.lastCidEndPoints;
			if (previousCidEndPoints === undefined) return; //Fonction de republi non active, on n'enregistre pas.
			const cidInfo = ctx.cidInfo;
			const metasToSave: Dict<any> = {};
			const allMetas = cidInfo.cidSession.metasOut;
			for (let metaDef of cidInfo.cidSession.cidProcess.metas.values()) {
				const val = allMetas[metaDef.name];
				if (val !== undefined && !metaDef.isExcludeForRecall) metasToSave[metaDef.name] = val;
			}
			const fd = new FormData();
			if (previousCidEndPoints instanceof Promise) previousCidEndPoints = await previousCidEndPoints; //pour le mvcc.
			fd.append("options", JSON.stringify({
				mvcc: previousCidEndPoints?.mvcc || 0,
				endPoints: [{
					idx: 0, //TODO multi manifestUrl
					manifestUrl: cidInfo.lastManifest.manifestUrl,
					cidProps: metasToSave
				}]
			}));
			const newCidEndPoints = await ctx.wsp.wspServer.config.wspSrcUrl.fetchJson<JCidEnPointsFields>(IO.qs("cdaction", "UpdateCidEndPoints", "fields", "cidEndPoints", "param", ctx.wsp.code, "refUri", SRC.srcRef(ctx.shortDesc)), {
				method: "POST",
				body: fd
			});
			if (newCidEndPoints) {
				ctx.cidInfo.lastCidEndPoints = newCidEndPoints.cidEndPoints;
				(ctx.uiContext as Generators).cidRefreshOnSessionChange();
			}
		} catch (e) {
			ERROR.log("Save last Cid deploy failed", e);
		}
	}

	/** Evalue si l'upload CID peut être directement envoyé par le serveur, ou si le téléchargement via le client s'impose. */
	static async getSendByServerBody(ctx: IPubCtx, session: CidSession, uploadStep: CidUploadStep, webUpload: ICidWebUploadDef): Promise<null | { method: string, url: string, headers: any }> {
		const webUploadMedia = webUpload.PUT || webUpload.POST;
		if (!webUploadMedia) return null;
		const resp = await ctx.reg.env.universe.httpFrames.web.fetchSvc("u/remoteContent", "CheckReachableUrls", {urls: CDM.stringify([uploadStep.uploadUrl])}, "json");
		if (resp.ok && resp.asJson[0]) {
			const sendOpts = {
				method: webUploadMedia === webUpload.PUT ? "PUT" : "POST"
			} as any;
			if (webUploadMedia.queryString) {
				sendOpts.url = CidUploadStep.enrichUrl(uploadStep.uploadUrl, session, uploadStep.def);
			} else {
				sendOpts.headerProps = CidUploadStep.enrichHeaders(session, uploadStep.def);
			}
			if (!sendOpts.headerProps) sendOpts.headerProps = {};
			if (!sendOpts.headerProps[IO.REQUEST_LANG_HEADER]) {
				const reqInit = IO.addLang();
				if (reqInit?.headers) sendOpts.headerProps[IO.REQUEST_LANG_HEADER] = (reqInit.headers as any)[IO.REQUEST_LANG_HEADER];
			}
			return sendOpts;
		}
		return null;
	}
}

export class CidScarReDeployAction extends CidScarDeployAction {

	_label = "Redéployer";
	_icon = "/@skin@/wsp/views/generators/resend.svg";

	static isCidEndPointAvailable(ctx: IPubCtx): boolean {
		const url = ctx.cidInfo.lastManifest.manifestUrl;
		const endPoints = ctx.cidInfo.lastCidEndPoints;
		if (endPoints === undefined) {
			//cidEndPoints pas chargés, on interroge...
			ctx.cidInfo.lastCidEndPoints = WSP.fetchSrc(ctx.wsp, ctx.uiContext, SRC.srcRef(ctx.shortDesc), ['cidEndPoints'] as (keyof JCidEnPointsFields)[] as any).then((fields: JCidEnPointsFields) => {
				//console.log("CidScarReDeployAction:::fields:::", fields);
				ctx.cidInfo.lastCidEndPoints = fields?.cidEndPoints || null;
				(ctx.uiContext as Generators).cidRefreshOnSessionChange();
				return ctx.cidInfo.lastCidEndPoints;
			}).catch((e) => {
				ctx.cidInfo.lastCidEndPoints = null;
				ERROR.logError(e);
				return null;
			});
			return false;
		} else if (endPoints === null) {
			//Aucun endPoints enregistrés
			return false;
		} else if (endPoints instanceof Promise) {
			//Chargement en cours, pas encore dispo.
			return false;
		}
		//XXX comment gérer le switch de serveur CID (url !=) mais données compatibles ? ajouter host.manifestUrlCompat(url):boolean ?
		return endPoints.endPoints.find((ep) => ep.manifestUrl === url) != null;
	}

	static createMetasInFromEndPoints(ctx: IPubCtx, metasIn: Dict<string>): Dict<string> {
		const cidField = ctx.cidInfo.lastCidEndPoints;
		if (cidField && !(cidField instanceof Promise)) {
			const url = ctx.cidInfo.lastManifest.manifestUrl;
			const endPoint = cidField.endPoints.find((ep) => ep.manifestUrl === url);
			if (endPoint) return Object.assign(metasIn, endPoint.cidProps);
		}
		return metasIn;
	}

	constructor() {
		super();
		this.passOptionalInteraction = true;
	}

	isVisible(ctx: IPubCtx): boolean {
		if (!super.isVisible(ctx)) return false;
		return CidScarReDeployAction.isCidEndPointAvailable(ctx);
	}

	createMetasIn(ctx: IPubCtx): Dict<string> {
		return CidScarReDeployAction.createMetasInFromEndPoints(ctx, super.createMetasIn(ctx));
	}

	overrideSession(config: OCidSessionConfig, ctx: IPubCtx): OCidSessionConfig {
		if (!config.foreignMetas)
			config.foreignMetas = [];
		config.foreignMetas.push({is: CID.IS_Resend, value: ''});

		return super.overrideSession(config, ctx);
	}
}

export class CidGenDeployAction extends CidDeployAction {

	async execute(ctx: IPubCtx, ev?: Event) {
		if (!await GenerateAction.checkSuperGen(ctx))
			return;
		return super.execute(ctx, ev);
	}

	async createCidSession(ctx: IPubCtx): Promise<CidSession> {
		const stack = ctx.pubNode.codeGenStack.split('_');
		//on interroge le type et le name pour le download
		const genCodeStack = stack.slice(0, stack.length - 1).join('_');
		const resp = await fetch(ITEM.getGenDownloadUrl(ctx.wsp, SRC.srcRef(ctx.shortDesc), genCodeStack), {method: "HEAD"});
		if (!resp.ok) {
			POPUP.showNotifInfo("Génération non disponible", ctx.uiContext);
			return null;
		}
		const fileName = extractFileNameFromContentDisposition(resp.headers.get("Content-Disposition"));
		const contentType = resp.headers.get("Content-Type");
		const metasIn = this.createMetasIn(ctx);
		const foreignMetas = [{is: CID.IS_FileName, value: fileName}, {is: CID.IS_ContentType, value: contentType}];
		if (await this.overrideMetas(ctx, metasIn, foreignMetas) === false) return;

		return new CidSession(this.overrideSession({
			manifest: ctx.cidInfo.lastManifest,
			uiHandler: ctx.uiContext as Generators,
			metasIn,
			foreignMetas,
			customUpload: async (session: CidSession, uploadStep: CidUploadStep, webUpload: ICidWebUploadDef): Promise<null | Response> => {
				const sendBySrv = await CidScarDeployAction.getSendByServerBody(ctx, session, uploadStep, webUpload);
				const params = IO.qs("cdaction", sendBySrv ? "SendGenTo" : "Download", "param", ctx.wsp.code, "refUri", SRC.srcRef(ctx.shortDesc), "codeGenStack", genCodeStack);
				const content = await ctx.wsp.wspServer.config.wspGenUrl.fetch(params, "none", sendBySrv ? {method: "PUT", body: JSON.stringify(sendBySrv)} : null);
				if (!content.ok) {
					//A voir... POPUP.showNotif("Génération non disponible", ctx.uiContext);
					return content;//new Response(null, {status: content.status}) ?
				}
				if (sendBySrv) return content; //Envoi par le serveur ok.
				session.config.bodyToUpload = await content.blob();
				return null; //déploiement standard à partir de bodyToUpload
			}
		}, ctx));
	}

	async saveLastDeploy(ctx: IPubCtx): Promise<void> {
		try {
			if (!ctx.wsp.isAvailable) return; //rupture de co => abandon
			let lastCidProps = ctx.cidInfo.lastCidProps;
			if (lastCidProps === undefined) return; //Fonction de republi non active, on n'enregistre pas.
			const cidInfo = ctx.cidInfo;
			const metasToSave: Dict<any> = {};
			const allMetas = cidInfo.cidSession.metasOut;
			for (let metaDef of cidInfo.cidSession.cidProcess.metas.values()) {
				const val = allMetas[metaDef.name];
				if (val !== undefined && !metaDef.isExcludeForRecall) metasToSave[metaDef.name] = val;
			}
			// if (lastCidProps instanceof Promise) lastCidProps = await lastCidProps;
			// if (lastCidProps) {
			// 		//TODO fusion si multi-store manifestUrl / cidProcessIs.
			// } else {
			ctx.cidInfo.lastCidProps = {
				store: [{
					manifestUrl: cidInfo.lastManifest.manifestUrl,
					cidProcessIs: cidInfo.cidSession?.cidProcess?.is,
					cidProps: metasToSave
				}]
			}
			//}
			ctx.reg.env.universe.httpFrames.web.fetchSvc(
				"u/genPropsStored", "SaveGenProps",
				IO.query(null, "param", ctx.wsp.code, "srcRef", SRC.srcRef(ctx.shortDesc), "codeGenStack", ctx.pubNode.codeGenStack),
				'none',
				{method: "PUT", body: JSON.stringify(ctx.cidInfo.lastCidProps)}
			);
			(ctx.uiContext as Generators).cidRefreshOnSessionChange();
		} catch (e) {
			ERROR.log("Save last Cid deploy failed", e);
		}
	}
}

/**
 * @see https://httpwg.org/specs/rfc6266.html#header.field.definition
 * @see https://www.rfc-editor.org/rfc/rfc2616.html#section-3.6
 *
 * NOTE: la variante filename* (https://datatracker.ietf.org/doc/html/rfc5987) non implémenté
 */
function extractFileNameFromContentDisposition(ctDisp?: string): string {
	if (!ctDisp) return "";
	const i = ctDisp.indexOf("filename=");
	if (i < 0) return "";
	let value = ctDisp.substring(i + 9).trim();
	if (value.startsWith('"')) {
		//Nom entre quote : quoted-string
		value = value.substring(1);
		for (let k = 0; k < value.length; k++) {
			switch (value[k]) {
			case '\\':
				value = value.substring(0, k) + value.substring(k + 1);
				break;
			case '"':
				return value.substring(0, k);
			}
		}
	} else {
		//pas de quote : token
		const end = value.indexOf(';');
		if (end >= 0) value = value.substring(0, end);
	}
	return value;
}


export class CidGenReDeployAction extends CidGenDeployAction {

	_label = "Redéployer";
	_icon = "/@skin@/wsp/views/generators/resend.svg";

	static isReDeployDataAvailable(ctx: IPubCtx): boolean {
		const url = ctx.cidInfo.lastManifest.manifestUrl;
		const lastCidProps = ctx.cidInfo.lastCidProps;
		if (lastCidProps === undefined) {
			//lastCidProps pas chargés, on interroge...
			ctx.cidInfo.lastCidProps = ctx.reg.env.universe.httpFrames.web.fetchSvcJson<JCidStoredProps>("u/genPropsStored", "GetGenProps", IO.query(null, "param", ctx.wsp.code, "srcRef", SRC.srcRef(ctx.shortDesc), "codeGenStack", ctx.pubNode.codeGenStack)).then((datas) => {
				ctx.cidInfo.lastCidProps = datas || null; // null = pas de lastCidProps
				(ctx.uiContext as Generators).cidRefreshOnSessionChange();
				return ctx.cidInfo.lastCidProps;
			}).catch((e) => {
				ctx.cidInfo.lastCidProps = null; //pas de lastCidProps
				ERROR.logError(e);
				return null;
			});
			return false;
		} else if (lastCidProps === null) {
			//Aucun lastCidProps enregistrés
			return false;
		} else if (lastCidProps instanceof Promise) {
			//Chargement en cours, pas encore dispo.
			return false;
		}
		//XXX comment gérer le switch de serveur CID (url !=) mais données compatibles ? ajouter host.manifestUrlCompat(url):boolean ?
		return lastCidProps.store.find((d) => d.manifestUrl === url) != null;
	}

	static createMetasInFromLast(ctx: IPubCtx, metasIn: Dict<string>): Dict<string> {
		const lastCidProps = ctx.cidInfo.lastCidProps;
		if (lastCidProps && !(lastCidProps instanceof Promise)) {
			const url = ctx.cidInfo.lastManifest.manifestUrl;
			const datas = lastCidProps.store.find((d) => d.manifestUrl === url);
			if (datas) return Object.assign(metasIn, datas.cidProps);
		}
		return metasIn;
	}

	constructor() {
		super();
		this.passOptionalInteraction = true;
	}

	isVisible(ctx: IPubCtx): boolean {
		if (!super.isVisible(ctx)) return false;
		return CidGenReDeployAction.isReDeployDataAvailable(ctx);
	}

	createMetasIn(ctx: IPubCtx): Dict<string> {
		return CidGenReDeployAction.createMetasInFromLast(ctx, super.createMetasIn(ctx));
	}

	overrideSession(config: OCidSessionConfig, ctx: IPubCtx): OCidSessionConfig {
		if (!config.foreignMetas) config.foreignMetas = [];
		config.foreignMetas.push({is: CID.IS_Resend, value: ''});
		return super.overrideSession(config, ctx);
	}
}


export class CidConsultLastAction extends CidAction {

	_label = "Consulter";
	_description = "Consulter la dernière ressource déployée";
	_icon = "/@skin@/wsp/views/generators/show.svg";

	constructor() {
		super();
		this.processFilter = CidAction.PRC_IS_CONSULT;
	}

	async execute(ctx: IPubCtx, ev?: Event) {
		ctx = clonePubCtx(ctx);
		ctx.cidInfo.cidSession = await this.createCidSession(ctx);
		if (!ctx.cidInfo.cidSession) return;
		await ctx.cidInfo.cidSession.doSession();
	}

	async createCidSession(ctx: IPubCtx): Promise<CidSession> {
		const metasIn = this.createMetasIn(ctx);
		const foreignMetas: { is: string[], value: string | ((session: CidSession) => string) }[] = [];
		if (await this.overrideMetas(ctx, metasIn, foreignMetas) === false) return;
		return new CidSession(this.overrideSession({
			manifest: ctx.cidInfo.lastManifest,
			uiHandler: ctx.uiContext as Generators,
			metasIn,
			foreignMetas,
		}, ctx));
	}

}

export class CidScarConsultAction extends CidConsultLastAction {

	isVisible(ctx: IPubCtx): boolean {
		if (!super.isVisible(ctx)) return false;
		return CidScarReDeployAction.isCidEndPointAvailable(ctx);
	}

	createMetasIn(ctx: IPubCtx): Dict<string> {
		return CidScarReDeployAction.createMetasInFromEndPoints(ctx, super.createMetasIn(ctx));
	}
}

export class CidGenConsultAction extends CidConsultLastAction {

	isVisible(ctx: IPubCtx): boolean {
		if (!super.isVisible(ctx)) return false;
		return CidGenReDeployAction.isReDeployDataAvailable(ctx);
	}

	createMetasIn(ctx: IPubCtx): Dict<string> {
		return CidGenReDeployAction.createMetasInFromLast(ctx, super.createMetasIn(ctx));
	}
}

/**
 *
 * @deprecated pour compat packs 6.0, à suppr en SC7
 */
export const CidConsultAction = CidScarConsultAction;

/**
 * Au démmarage d'une génération, d'un déploiement (...) clone le IPubCtx pour le geler
 * afin qu'il ne dépende plus d'un changement de contexte graphique pendant le traitement (changement de IPubCtx.pubNode).
 */
function clonePubCtx(ctx: IPubCtx) {
	return Object.assign({}, ctx);
}